import axios from 'axios';
import { logInfo, logWarning } from 'lib/util/errors';

export interface ProfileData {
  address?: string;
  guestName?: string;
  guestFName?: string;
  guestLName?: string;
  guestJobTitle?: string;
  guestEmail?: string;
  guestPhone?: string;
  guestFax?: string;
  guestCompany?: string;
  guestCity?: string;
  guestState?: string;
  guestZip?: string;
  guestCenterData?: string;
}
export interface SalesForceData {
  profileData: ProfileData;
}
declare global {
  // boxever window properties are defined as any
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    _boxever: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Boxever: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    _boxeverq: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    _boxever_settings: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    _eventSettings: { LogEvents: boolean; Currency: string; Language: string; Channel: string };
  }
}
enum EventType {
  View = 'VIEW',
  Add = 'ADD',
  Confirm = 'CONFIRM',
  Checkout = 'CHECKOUT',
  Identity = 'IDENTITY',
  Search = 'SEARCH',
  Payment = 'PAYMENT',
}
const warn = (s: string) => {
  if (process.env.NODE_ENV === 'development') {
    logWarning('[react-sitecore-personalize]..', s);
  }
};
// Events can be many many types
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function logEvent(eventType: EventType, eventData: any) {
  if (!window._eventSettings.LogEvents) {
    return;
  }
  if (process.env.NODE_ENV === 'development') {
    logInfo(`Sending ${eventType} Event`, eventData);
  }
}

// Value can be of many many types
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function validateParam(object: any, paramName: string, paramValue: any) {
  const returnObject = { ...object };
  if (typeof paramValue !== 'undefined') {
    returnObject[paramName] = paramValue;
  }
  return returnObject;
}

function directClientScriptTags({
  clientKey,
  apiEndpoint,
  cookieDomain,
  clientVersion,
  pos,
  webFlowTarget,
}: // eslint-disable-next-line @typescript-eslint/no-explicit-any
any) {
  if (!clientKey || clientKey.length === 0) {
    warn('Client Key is required');
  }
  if (!apiEndpoint || apiEndpoint.length === 0) {
    warn('Api Endpoint is required');
  }
  if (!clientVersion || clientVersion.length === 0) {
    warn('Client Version is required');
  }
  if (!pos || pos.length === 0) {
    warn('Point of Sale is required');
  }

  const script =
    `var _boxeverq=_boxeverq||[],` +
    `_boxever_settings={client_key:"${clientKey}",target:"${apiEndpoint}",cookie_domain:"${cookieDomain}",pointOfSale:"${pos}",` +
    `web_flow_target:"${webFlowTarget}",javascriptLibraryVersion: "${clientVersion}",web_flow_config: { async: false, defer: false }};` +
    `!function(){var e=document.createElement("script");` +
    `e.type="text/javascript",e.async=!0,e.src="https://d1mj578wat5n4o.cloudfront.net/boxever-${clientVersion}.min.js";` +
    `var t=document.getElementsByTagName("script")[0];` +
    `t.parentNode.insertBefore(e,t)}();`;
  return {
    script,
  };
}

const DirectClientScript = {
  tags: directClientScriptTags,
};
function baseEvent(page: string, type: string) {
  interface LooseObject {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any;
  }
  const baseEvent1: LooseObject = {
    browser_id: window.Boxever.getID(),
    pos: window._boxever_settings.pointOfSale,
    channel: window._eventSettings.Channel,
    language: window._eventSettings.Language,
    currency: window._eventSettings.Currency,
    type: type,
    page: page,
  };
  return baseEvent1;
}

export function initClientScript(
  clientKey: string,
  cookieDomain: string,
  apiEndpoint: string,
  clientVersion: string,
  pointOfSale: string,
  webFlowTarget: string,
  eventSettings: { LogEvents: boolean; Currency: string; Language: string; Channel: string }
) {
  window._eventSettings = eventSettings;
  const existingScript = document.getElementById('boxeverclientscript');
  if (!existingScript) {
    const cdpSnippet = DirectClientScript.tags({
      clientKey: clientKey,
      apiEndpoint: apiEndpoint,
      cookieDomain: cookieDomain,
      clientVersion: clientVersion,
      pos: pointOfSale,
      webFlowTarget: webFlowTarget,
    });
    const script = () => {
      const script1 = document.createElement('script');
      script1.id = 'boxeverclientscript';
      script1.innerHTML = cdpSnippet.script;
      return script1;
    };
    document.body.appendChild(script());
  }
}

export function sendViewEvent(page: string) {
  if (!window._boxeverq) {
    return;
  }
  const pushToCdp = () => {
    let viewEvent = baseEvent(page, EventType.View);
    viewEvent = window.Boxever.addUTMParams(viewEvent);
    window.Boxever.eventCreate(
      viewEvent,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars, func-names
      function (data: any) {
        // do nothing
      },
      'json'
    );
    logEvent(EventType.View, viewEvent);
  };
  window._boxeverq.push(pushToCdp);
  if (window._boxever) {
    window._boxever.triggerExperiences();
  }
}
export async function setGuestExtData(
  extDataValue: string,
  apiEndPoint: string,
  apiKey: string,
  apiToken: string
) {
  /* const response = */ await axios.post(
    `${apiEndPoint}/extext/`,
    {
      key: 'default',
      format: 'HTML',
      authClaims: extDataValue,
      shortDescription: 'user claims',
      longDescription: 'user claims information',
    },
    {
      auth: {
        username: apiKey,
        password: apiToken,
      },
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );
}

async function getAndSetGuestProfile(
  apiEndPoint: string,
  apiKey: string,
  apiToken: string,
  guestEmail: string,
  authData: string
) {
  axios
    .get(`${apiEndPoint}/v2/guests?email=${guestEmail}`, {
      auth: {
        username: apiKey,
        password: apiToken,
      },
    })
    // OK to use anonymous functions with get/then pattern
    // eslint-disable-next-line func-names
    .then(function (response) {
      const apiUrl = response.data.items[0].href;
      if (apiUrl !== '') {
        setGuestExtData(authData, apiUrl, apiKey, apiToken);
      }
    });
}

export function sendIdentityByEmailEvent(
  page: string,
  email: string,
  clientKey: string,
  apiToken: string,
  title?: string,
  firstName?: string,
  authData?: string,
  lastName?: string,
  gender?: string,
  dob?: string,
  mobile?: string,
  phone?: string,
  street?: string,
  city?: string,
  state?: string,
  country?: string,
  postalCode?: string
) {
  if (!window._boxeverq) return;
  const sendIdentityToCdp = () => {
    // CDP defines callback param as any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const sendGuestProfileToCdp = (data: any) => {
      if (data.status === 'OK') {
        getAndSetGuestProfile(
          'https://api-us.boxever.com',
          clientKey,
          apiToken,
          email,
          authData || ''
        );
      }
    };
    let identityEvent = baseEvent(page, EventType.Identity);
    identityEvent.identifiers = [
      {
        provider: 'email',
        id: email,
      },
    ];
    identityEvent.email = email;
    identityEvent = validateParam(identityEvent, 'title', title);
    identityEvent = validateParam(identityEvent, 'firstName', firstName);
    identityEvent = validateParam(identityEvent, 'lastName', lastName);
    identityEvent = validateParam(identityEvent, 'gender', gender);
    identityEvent = validateParam(identityEvent, 'dob', dob);
    identityEvent = validateParam(identityEvent, 'mobile', mobile);
    identityEvent = validateParam(identityEvent, 'phone', phone);
    identityEvent = validateParam(identityEvent, 'city', city);
    identityEvent = validateParam(identityEvent, 'state', state);
    identityEvent = validateParam(identityEvent, 'country', country);
    identityEvent = validateParam(identityEvent, 'postal_code', postalCode);
    identityEvent = validateParam(identityEvent, 'ext.authData', authData);
    logEvent(EventType.Identity, identityEvent);
    window.Boxever.eventCreate(identityEvent, sendGuestProfileToCdp, 'json');
  };
  window._boxeverq.push(sendIdentityToCdp);
}
export const setCookie = (name: string, value: string, expMin?: number): void => {
  if (typeof document === 'undefined') {
    return;
  }

  if (!expMin) {
    document.cookie = `${name}=${value}; path=/`;
    return;
  }

  const date = new Date();
  date.setTime(date.getTime() + expMin * 60 * 1000);
  const expires = `expires= ${date.toUTCString()}`;
  document.cookie = `${name}=${value}; ${expires}; path=/`;
};

export const getCookie = (name: string): string | null => {
  if (typeof document === 'undefined') {
    return null;
  }

  const key = `${name}=`;
  const cDecoded = decodeURIComponent(document.cookie);
  const cArr = cDecoded.split('; ');
  let res = null;
  cArr.forEach((val) => {
    if (val.indexOf(key) === 0) {
      res = val.substring(key.length);
    }
  });
  return res;
};

export function getSalesforceFlowData(): Promise<SalesForceData> {
  const callFlowsContext = {
    context: {
      channel: 'WEB',
      language: 'en',
      currencyCode: 'USD',
      pointOfSale: window.Boxever.pointOfSale,
      browserId: window.Boxever.getID(),
      clientKey: window.Boxever.client_key,
      friendlyId: 'salesforcedataweb',
    },
  };

  return new Promise((resolve) => {
    window.Boxever.callFlows(
      callFlowsContext,
      (response: SalesForceData) => {
        resolve(response);
      },
      'json'
    );
  });
}
